import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Column, EmptyState, FlexBox } from '@dixa/lib-shared-habla';
import { useCallback, useMemo, useState } from 'react';
import { analytics } from 'app/utils/segment';
import { ReviewConflictsModal } from 'components/settings/components/sections/openingHours/components/reviewConflictsModal/reviewConflictsModal';
import { getLocalDate } from 'components/settings/components/sections/openingHours/utils/date';
import { ListItemType, useScheduleContext, } from 'components/settings/components/sections/openingHours/views/schedule/schedule.context';
import { validateIntervals } from '../../../../utils/validate';
import { ListHeader } from '../listHeader/listHeader';
import { ListItem } from '../listItem/listItem';
import { ListItemContext } from '../listItem/listItem.context';
import { AddExceptionModal } from './components/addExceptionModal/addExceptionModal';
const headers = [
    {
        label: 'Exceptions (optional)',
        subLabel: 'sorted by upcoming date',
    },
    {
        label: 'Custom audio messages (optional)',
        subLabel: 'sorted by upcoming date',
        helpText: `These replace the standard audio message. If set, customers will hear the audio
      messages when they call you outside the opening hours of the exception,
      e.g. during the holidays. This applies to all phone flows which use this schedule.`,
    },
    {
        label: 'Custom auto-replies (optional)',
        subLabel: 'sorted by upcoming date',
        helpText: `These replace the standard auto-reply. If set, customer will receive
      these auto-replies when they email you outside the opening hours of the exception,
      e.g. during the holidays. This applies to all email flows which use this schedule.`,
    },
];
export const ExceptionsList = () => {
    const [activeModalType, setActiveModalType] = useState(null);
    const [resolvedException, setResolvedException] = useState(null);
    const { intervalsErrors: { [ListItemType.Exception]: intervalsErrors }, listItems: { [ListItemType.Exception]: listItems }, setIntervalsErrors, setModifiedListItems, } = useScheduleContext();
    const handleValidateInterval = useCallback((dayIndex) => (intervals) => void setIntervalsErrors(ListItemType.Exception, validateIntervals({
        intervals,
        dayIndex,
        errors: intervalsErrors,
    })), [intervalsErrors, setIntervalsErrors]);
    const handleAddNewException = useCallback((exception) => void setModifiedListItems(ListItemType.Exception, {
        [exception.id]: exception,
    }), [setModifiedListItems]);
    const handleIntervalsChange = useCallback((id) => (intervals) => {
        const exception = listItems.find(({ id: exceptionId }) => exceptionId === id);
        setModifiedListItems(ListItemType.Exception, {
            [id]: Object.assign(Object.assign({}, exception), { intervals }),
        });
    }, [listItems, setModifiedListItems]);
    const handleAudioMessageChange = useCallback((id) => (audioMessage) => {
        const exception = listItems.find((exc) => exc.id === id);
        setModifiedListItems(ListItemType.Exception, {
            [id]: Object.assign(Object.assign({}, exception), { announcement: audioMessage }),
        });
    }, [listItems, setModifiedListItems]);
    const handleAutoReplyChange = useCallback((id) => (autoReply) => {
        const exception = listItems.find((exc) => exc.id === id);
        setModifiedListItems(ListItemType.Exception, {
            [id]: Object.assign(Object.assign({}, exception), { auto_reply_template_id: autoReply }),
        });
    }, [listItems, setModifiedListItems]);
    const handleDelete = useCallback((id) => () => {
        const exception = listItems.find((exc) => exc.id === id);
        setModifiedListItems(ListItemType.Exception, {
            [id]: Object.assign(Object.assign({}, exception), { archived: getLocalDate().toISOString() }),
        });
    }, [listItems, setModifiedListItems]);
    const handleResolveConflicts = useCallback((modifiedItems) => {
        setModifiedListItems(ListItemType.Exception, modifiedItems);
        setResolvedException(null);
    }, [setModifiedListItems]);
    const handleOverriddenChange = useCallback((listItem) => {
        setResolvedException(listItem);
        setActiveModalType('review');
    }, []);
    const hasExceptions = !!(listItems === null || listItems === void 0 ? void 0 : listItems.length);
    const context = useMemo(() => ({
        intervalsSelectProps: {
            errors: intervalsErrors,
            onChange: handleIntervalsChange,
            validate: handleValidateInterval,
        },
        onAudioMessageChange: handleAudioMessageChange,
        onAudioMessageClear: handleAudioMessageChange,
        onAutoReplyChange: handleAutoReplyChange,
        onAutoReplyClear: handleAutoReplyChange,
        onDelete: handleDelete,
        onOverriddenChange: handleOverriddenChange,
    }), [
        intervalsErrors,
        handleAudioMessageChange,
        handleAutoReplyChange,
        handleDelete,
        handleIntervalsChange,
        handleOverriddenChange,
        handleValidateInterval,
    ]);
    return (_jsxs(_Fragment, { children: [hasExceptions ? (_jsxs(_Fragment, { children: [_jsx(ListHeader, Object.assign({ headers: headers }, { children: _jsx(Button, Object.assign({ onClick: () => {
                                setActiveModalType('add');
                                analytics.businessHoursScheduleAddExceptionButtonClicked();
                            }, variant: "secondary" }, { children: "Add exception" })) })), _jsx(ListItemContext.Provider, Object.assign({ value: context }, { children: _jsx(Column.Row, Object.assign({ scrollable: true, noVerticalPadding: true }, { children: _jsx(FlexBox, Object.assign({ flexDirection: "column", gap: "250", padding: "0 0 300" }, { children: listItems.map((listItem, index) => (_jsx(ListItem, { index: index, item: listItem }, listItem.id))) })) })) }))] })) : (_jsx(Column.Row, Object.assign({ size: 1 }, { children: _jsx(FlexBox, Object.assign({ height: "100%", justifyContent: "center", alignItems: "center" }, { children: _jsx(EmptyState, { buttonProps: {
                            children: 'Add exception',
                            variant: 'secondary',
                            onClick: () => setActiveModalType('add'),
                        }, description: "Add exceptions for days when you\u2019re closed or have different business hours!", icon: "calendar-times", iconColor: "neutral.700", title: "Gone fishin\u2019?" }) })) }))), activeModalType === 'add' && (_jsx(AddExceptionModal, { exceptions: listItems, onModalClose: () => setActiveModalType(null), onSubmit: handleAddNewException })), activeModalType === 'review' && resolvedException && (_jsx(ReviewConflictsModal, { conflicts: listItems.reduce((acc, curr) => (Object.assign(Object.assign({}, acc), (curr.next_date === resolvedException.next_date && {
                    [curr.id]: curr,
                }))), {}), onClose: () => setActiveModalType(null), onResolve: handleResolveConflicts, resolvedDates: [resolvedException.next_date] }))] }));
};
